import {
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useImsApiContext } from '@/auth/ImsApiContext.tsx';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger.ts';

import { useBridgewayApi } from './BridgewayApi.ts';
import { ExpressAPI, checkHasValidInstagramConnection } from './ExpressApi.ts';
import {
	ExpressApiContext,
	RECONCILE_MIN_INTERVAL_MS,
	RECONCILE_RETRY_MIN_INTERVAL_MS,
} from './ExpressApiContext.tsx';

const CONNECT_TIMEOUT_MS = 5 * 60 * 1000;

export const ExpressApiProvider = ({
	children,
}: {
	children: ReactNode;
}): ReactElement => {
	const imsApiContext = useImsApiContext();
	const imsToken = imsApiContext?.imsToken ?? null;
	const userId = imsApiContext?.userId ?? null;

	const bridgewayApi = useBridgewayApi();

	const [expressApi, setExpressApi] = useState<ExpressAPI | null>(null);
	const [isReady, setIsReady] = useState(false);

	const { settings, patchSettings } = useUserSettings();

	// The last call that actually did the work. Only such a call arms the long
	// throttle, and `lastReconcileAt` reports this instant.
	const lastReconcileAtRef = useRef(0);
	// The last call that reached the route, whatever it answered. It carries the
	// much shorter RECONCILE_RETRY_MIN_INTERVAL_MS, so a failed look cannot hold
	// the customer's next gesture for ten minutes.
	const lastAttemptAtRef = useRef(0);

	// Both throttles gate a call keyed to the current user. A `userId` change
	// without a full provider remount (an in-session account switch) must not
	// let a previous user's timestamps throttle away the new user's first
	// reconcile — that reproduces this ticket's own defect for the new user.
	useEffect(() => {
		lastReconcileAtRef.current = 0;
		lastAttemptAtRef.current = 0;
	}, [userId]);

	// Read through a callback, not as a value: the instant changes on every
	// reconcile, and a value on the context would repaint every consumer.
	const lastReconcileAt = useCallback(() => lastReconcileAtRef.current, []);

	// Asks the server to look at every scheduled post again. It answers true only
	// when the call actually ran, so a caller that is waiting for a fresh answer
	// can re-arm rather than count a look it never got.
	//
	// It swallows its own errors on purpose. A caller must never let a failed
	// re-check disable scheduling for the session.
	const reconcileInstagramPosts = useCallback(async (): Promise<boolean> => {
		if (!userId) return false;
		const now = Date.now();
		if (
			now - lastReconcileAtRef.current < RECONCILE_MIN_INTERVAL_MS ||
			now - lastAttemptAtRef.current < RECONCILE_RETRY_MIN_INTERVAL_MS
		) {
			return false;
		}
		// Recorded before the request, so two callers on one focus event cannot
		// both reach the route while the first is still in flight.
		lastAttemptAtRef.current = now;
		try {
			await bridgewayApi.post(`/api/user/${userId}/instagram/resolve`, {});
			// Only here. A failed request queues none of the work the long throttle
			// exists to bound. Armed on a failure, it held a stale label on screen
			// for ten minutes and bought nothing.
			lastReconcileAtRef.current = now;
			return true;
		} catch (error) {
			logger.error('Instagram reconcile failed', error);
			return false;
		}
	}, [userId, bridgewayApi]);

	// The initialize effect below reads the callback through a ref so the
	// callback's identity stays out of its dependency list. That effect calls
	// patchSettings and rebuilds ExpressAPI, and its catch runs setExpressApi(null)
	// and setIsReady(false), which disables every scheduling path for the session.
	// React may discard a useCallback cache at any time, so a re-run must not
	// depend on that.
	const reconcileRef = useRef(reconcileInstagramPosts);
	useEffect(() => {
		reconcileRef.current = reconcileInstagramPosts;
	}, [reconcileInstagramPosts]);

	useEffect(() => {
		const initialize = async () => {
			if (!imsToken) {
				setExpressApi(null);
				setIsReady(false);
				return;
			}

			try {
				const api = new ExpressAPI(imsToken);
				setExpressApi(api);

				const calendars = await api.getCalendars();

				if (calendars.length > 0) {
					// TODO: support multiple calendars
					const calendar = calendars[0];
					await patchSettings({
						express_connection: {
							calendar: [
								{
									id: calendar.id,
									name: calendar.name,
									channels: calendar.channels,
								},
							],
						},
					});
				} else {
					await patchSettings({ express_connection: null });
					logger.info('No Express calendars found');
				}

				setIsReady(true);

				const hasInstagram =
					calendars.length > 0 && checkHasValidInstagramConnection(calendars);

				if (userId && hasInstagram) {
					void reconcileRef.current();
				}
			} catch (error) {
				logger.error('Error initializing Express API:', error);
				// Keep the client. It needs only the IMS token, so a failed
				// calendars read or settings write says nothing about it. Clearing
				// it here left Delete and Unschedule live with no way to reach
				// Express, so the owner of a queued post could not remove it.
				setIsReady(false);
			}
		};

		void initialize();
	}, [imsToken, userId, bridgewayApi, patchSettings]);

	const hasValidInstagramConnection = useMemo(
		() =>
			checkHasValidInstagramConnection(settings?.express_connection?.calendar),
		[settings?.express_connection],
	);

	// A customer who leaves the tab and comes back has given the server time to
	// publish a post that was still pending when they left. Look again.
	useEffect(() => {
		if (!hasValidInstagramConnection) return;
		const handleFocus = () => {
			void reconcileInstagramPosts();
		};
		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, [hasValidInstagramConnection, reconcileInstagramPosts]);

	const [isConnecting, setIsConnecting] = useState(false);

	const beginExpressConnection = useCallback(async () => {
		if (!expressApi) return;
		setIsConnecting(true);
		const popup = await expressApi.initExpressConnection();
		if (!popup) {
			setIsConnecting(false);
		}
	}, [expressApi]);

	const lastRefreshRef = useRef(0);
	const connectStartedAtRef = useRef(0);

	useEffect(() => {
		if (!isConnecting) return;

		connectStartedAtRef.current = Date.now();

		const handleFocus = async () => {
			if (!expressApi) return;

			if (Date.now() - connectStartedAtRef.current > CONNECT_TIMEOUT_MS) {
				cleanup();
				setIsConnecting(false);
				return;
			}

			const now = Date.now();
			if (now - lastRefreshRef.current < 5000) return;
			lastRefreshRef.current = now;

			try {
				const calendars = await expressApi.getCalendars();
				if (calendars.length > 0) {
					const calendar = calendars[0];
					await patchSettings({
						express_connection: {
							calendar: [
								{
									id: calendar.id,
									name: calendar.name,
									channels: calendar.channels,
								},
							],
						},
					});
					if (checkHasValidInstagramConnection([calendar])) {
						cleanup();
						setIsConnecting(false);
					}
				} else {
					await patchSettings({ express_connection: null });
				}
			} catch (error) {
				logger.error('Error refreshing Express connection:', error);
			}
		};

		const cleanup = () => window.removeEventListener('focus', handleFocus);

		window.addEventListener('focus', handleFocus);
		return cleanup;
	}, [isConnecting, expressApi, patchSettings]);

	return (
		<ExpressApiContext.Provider
			value={{
				expressApi,
				isReady,
				hasValidInstagramConnection,
				beginExpressConnection,
				reconcileInstagramPosts,
				lastReconcileAt,
			}}
		>
			{children}
		</ExpressApiContext.Provider>
	);
};
